import { Mail, Users, Podcast, Search, Globe, Ban } from 'lucide-react';
import type { PackageConfig } from '../../types';
import type { BuyerProxyResult } from '../../lib/buyerProxy';

interface Props {
 config: PackageConfig;
 buyerProxy: BuyerProxyResult;
}

interface ChannelRec {
 name: string;
 icon: React.ReactNode;
 why: string;
 messageAngle: string;
 proof: string;
}

function buildChannels(config: PackageConfig, proxy: BuyerProxyResult) {
 const d = proxy.demographics;
 const topWtp = d.topWtpFactors[0];
 const awareness = d.brandAwareness.neverHeardPercent;
 const isFemaleSkew = d.femalePercent > 55;
 const isHighPrice = config.price >= 999;

 const primary: ChannelRec[] = [
 {
 name: 'Facebook / Instagram Ads',
 icon: <Globe size={14} />,
 why: `${d.femalePercent}% female, age ${d.ageRange[0]}–${d.ageRange[1]} — peak engagement demographic for Meta platforms.`,
 messageAngle: 'Before/after transformation stories with data proof points',
 proof: topWtp ? `Q8: ${topWtp.percentage}% want "${topWtp.label}" — visual proof performs` : 'Visual proof resonates with this demographic',
 },
 {
 name: 'Email Nurture Sequences',
 icon: <Mail size={14} />,
 why: isHighPrice
 ? `Considered purchase at $${config.price.toLocaleString()} requires education and trust-building.`
 : `Even at $${config.price.toLocaleString()}, email builds understanding of the integrated system.`,
 messageAngle: 'Feature-by-feature value explanation over 3-email drip',
 proof: d.topWtpFactors[1] ? `Q8: ${d.topWtpFactors[1].percentage}% want "${d.topWtpFactors[1].label}" — drip builds understanding` : 'Multi-touch education drives conversion',
 },
 {
 name: 'Health & Wellness Influencers',
 icon: <Users size={14} />,
 why: `${awareness}% have never heard of AlterMe (Q12) — need awareness from trusted voices.`,
 messageAngle: 'Authentic experience stories from credible health voices',
 proof: 'Cold market needs trust signals before conversion',
 },
 ];

 const secondary: ChannelRec[] = [
 {
 name: 'Podcast Sponsorship',
 icon: <Podcast size={14} />,
 why: `Health/wellness podcasts index high with ${isFemaleSkew ? 'female' : 'mixed'}, ${d.ageRange[0]}-${d.ageRange[1]} demographic.`,
 messageAngle: 'Host-read endorsements with personal health angle',
 proof: `Median age ${d.medianAge} aligns with podcast listener demographics`,
 },
 {
 name: 'SEO / Content Marketing',
 icon: <Search size={14} />,
 why: 'Target health goal keywords from Q1 verbatims (weight loss, fitness, nutrition).',
 messageAngle: 'Educational content addressing top health goals',
 proof: `Top goals: ${d.topGoals.slice(0, 2).map(g => g.label).join(', ')}`,
 },
 {
 name: 'Google Ads',
 icon: <Globe size={14} />,
 why: 'Branded + category terms capture active searchers.',
 messageAngle: 'Direct response with price and feature count',
 proof: 'Captures demand from awareness campaigns',
 },
 ];

 const antiPatterns: ChannelRec[] = [
 {
 name: 'TikTok',
 icon: <Ban size={14} />,
 why: `Median age ${d.medianAge} — not a TikTok-first audience. Platform skews younger.`,
 messageAngle: '',
 proof: 'Demographic mismatch',
 },
 {
 name: 'LinkedIn',
 icon: <Ban size={14} />,
 why: 'B2C wellness product, not a professional tool. Wrong context.',
 messageAngle: '',
 proof: 'Category mismatch',
 },
 {
 name: 'Print Media',
 icon: <Ban size={14} />,
 why: 'Digital-first audience based on survey completion behavior.',
 messageAngle: '',
 proof: 'Channel efficiency mismatch',
 },
 ];

 return { primary, secondary, antiPatterns };
}

function ChannelCard({ channel, variant }: { channel: ChannelRec; variant: 'primary' | 'secondary' | 'anti' }) {
 return (
 <div className={`p-3 rounded-lg border ${variant === 'anti' ? 'border-metric-negative/20 bg-metric-negative/5' : 'border-border'}`}>
 <div className="flex items-center gap-2 mb-1.5">
 <span className={variant === 'anti' ? 'text-red-400' : 'text-chart-teal'}>{channel.icon}</span>
 <span className="text-xs font-semibold text-primary">{channel.name}</span>
 </div>
 <p className="text-sm text-secondary mb-1">{channel.why}</p>
 {channel.messageAngle && (
 <p className="text-sm text-muted"><span className="font-medium">Angle:</span> {channel.messageAngle}</p>
 )}
 {channel.proof && (
 <p className="text-xs text-chart-teal italic mt-0.5">{channel.proof}</p>
 )}
 </div>
 );
}

export function ChannelStrategy({ config, buyerProxy }: Props) {
 const { primary, secondary, antiPatterns } = buildChannels(config, buyerProxy);

 return (
 <div className="card mb-6">
 <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider mb-4">
 Channel Strategy — {config.name}
 </h3>

 <div className="mb-5">
 <div className="text-xs font-semibold uppercase tracking-wider text-chart-teal mb-2">Primary Channels (Highest Expected ROI)</div>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
 {primary.map(c => <ChannelCard key={c.name} channel={c} variant="primary" />)}
 </div>
 </div>

 <div className="mb-5">
 <div className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">Secondary Channels</div>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
 {secondary.map(c => <ChannelCard key={c.name} channel={c} variant="secondary" />)}
 </div>
 </div>

 <div>
 <div className="text-xs font-semibold uppercase tracking-wider text-red-400 mb-2">Anti-Patterns (Avoid for This Audience)</div>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
 {antiPatterns.map(c => <ChannelCard key={c.name} channel={c} variant="anti" />)}
 </div>
 </div>

 <p className="text-micro text-muted italic mt-4">
 Channel recommendations derived from buyer proxy demographics (n={buyerProxy.count}) and Q8/Q12 verbatim analysis. Adjust based on actual campaign performance data.
 </p>
 </div>
 );
}
